<template>
  <div
    ref="trigger"
    v-on-click-outside="handleClickOutside"
    class="dropdown"
    :class="{ 'dropdown-open': open, 'table-actions': type === 'actions' }"
    @click="changeOpenState"
  >
    <slot name="trigger">
      <button
        type="button"
        class="dropdown-trigger"
        :class="triggerStyles"
      >
        <span>{{ type === 'actions' ? 'Actions' : triggerText }}</span>

        <FontAwesomeIcon :icon="icon || type === 'actions' ? faCaretDown : faAngleDown" />
      </button>
    </slot>

    <div
      v-show="open"
      ref="floating"
      :style="floatingStyles"
      class="dropdown-content"
    >
      <slot>
        <template
          v-for="(item, index) in items"
          :key="index"
        >
          <AppDropdownItem
            :to="item.to"
            @click="open = false"
          >
            {{ item.label }}
          </AppDropdownItem>
        </template>
      </slot>
    </div>
  </div>
</template>

<script setup>
import { autoUpdate, flip, offset, size, useFloating } from '@floating-ui/vue';
import { computed, ref } from 'vue';

import { faCaretDown, faAngleDown } from '@fortawesome/sharp-solid-svg-icons';
import { vOnClickOutside } from '@vueuse/components';
import { AppDropdownItem } from '@/components/dropdowns';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const props = defineProps({
  triggerText: {
    type: String,
    default: '',
  },
  triggerStyles: {
    type: [String, Object, Array],
    default: () => [],
  },
  placement: {
    type: String,
    default: 'bottom-start',
    validator: (value) => ['top-start', 'top-middle', 'top-end', 'bottom-start', 'bottom-middle', 'bottom-end'].includes(value),
  },
  items: {
    type: Array,
    default: () => [],
  },
  icon: {
    type: Object,
    default: null,
  },
  type: {
    type: String,
    default: 'normal',
  },
  disabled: Boolean,
});

const trigger = ref();
const floating = ref();

const open = ref(false);

const handleClickOutside = () => {
  if (open.value) {
    open.value = false;
  }
};

const changeOpenState = () => {
  if (!props.disabled) {
    open.value = !open.value;
  }
};

const floatingMiddleware = computed(() => {
  const baseMiddleware = [offset(5), flip()];

  baseMiddleware.push(
    size({
      apply({ rects, elements }) {
        Object.assign(elements.floating.style, {
          width: `${rects.reference.width}px`,
        });
      },
    }),
  );

  return baseMiddleware;
});

const { floatingStyles } = useFloating(trigger, floating, {
  placement: props.placement,
  strategy: 'fixed',
  whileElementsMounted: autoUpdate,
  middleware: floatingMiddleware,
});

</script>
